import React, { useCallback } from 'react';
import type { PrimitiveType } from 'intl-messageformat';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { dismissFlag as createDismissFlag, useFlagService } from '@atlassian/jira-flags';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	type AnalyticsAttributes,
} from '@atlassian/jira-product-analytics-bridge';
import { useIntl, type MessageDescriptor } from '@atlassian/jira-intl';
import type { Collaborators } from '../../services/get-collaborators/types.tsx';
import { generateId } from '../../utils/index.tsx';
import { messages } from '../../common/ui/request-access-prompt-flag/messages.tsx';

import { getFeatureVisitedCount, VISITED_MSR_COUNT_KEY } from '../../services/caching/index.tsx';
import { MsrRequestAccessPromptFlagCommon, type CJNMessageStrings } from './msr-flag-common.tsx';

export const useMsrRequestAccessPromptFlag = () => {
	const { showFlag, dismissFlag } = useFlagService();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const accountId = useAccountId() || '';

	return useCallback(
		async ({
			users,
			onSubmit,
			eventAttributes,
			messageParser,
		}: {
			users: Collaborators;
			onSubmit: () => Promise<void>;
			messageParser: (formatter: typeof formatMessage) => CJNMessageStrings;
			eventAttributes: AnalyticsAttributes;
		}) => {
			const id = `request-access-${generateId()}`;

			const dismissTheFlag = () => dismissFlag(createDismissFlag(id));

			const submit = async () => {
				fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', eventAttributes);
				dismissTheFlag();
				await onSubmit();
			};

			const dismiss = () => {
				fireUIAnalytics(createAnalyticsEvent({}), 'flag dismissed', eventAttributes);
				dismissTheFlag();
			};

			const dismissAndSnooze = () => {
				fireUIAnalytics(createAnalyticsEvent({}), 'flag snoozed', eventAttributes);
				dismissTheFlag();
			};

			fireUIAnalytics(createAnalyticsEvent({}), 'nudge viewed', eventAttributes);

			const cjnMessages = messageParser(formatMessage);
			const featureVisitedCount = await getFeatureVisitedCount(accountId, VISITED_MSR_COUNT_KEY);

			showFlag({
				id,
				render: (props) => (
					<MsrRequestAccessPromptFlagCommon
						{...props}
						id={id}
						users={users}
						onDismiss={dismiss}
						onSnoozeAndDismiss={dismissAndSnooze}
						onSubmit={submit}
						messages={cjnMessages}
						featureVisitedCount={featureVisitedCount}
					/>
				),
			});
		},
		[formatMessage, createAnalyticsEvent, dismissFlag, showFlag, accountId],
	);
};

export function secondsElapsedSince(timestampInMs: number) {
	const currentTime = Date.now();
	return ((currentTime - timestampInMs) / 1000).toFixed(3);
}

type CommonAnalyticsArgs = {
	users: Collaborators;
	userId: string;
	sessionId?: string;
	featureVisitedCount?: number;
	startTimestamp: number;
};

export const getAnalyticsAttributes = ({
	users,
	userId,
	sessionId,
	startTimestamp,
}: CommonAnalyticsArgs): AnalyticsAttributes => {
	return {
		collaborators: users.length,
		userId,
		sessionId,
		eligibilityLatencySeconds: secondsElapsedSince(startTimestamp),
	};
};

export const getJPDCJNMessageStrings = (
	formatMessage: (md: MessageDescriptor, values?: Record<string, PrimitiveType>) => string,
): CJNMessageStrings => {
	return {
		// TODO: create new messages for MSR
		title: formatMessage(messages.titleJpdNudge),
		description: formatMessage(messages.descriptionJpdNudge),
		joinButton: formatMessage(messages.getStartedButton),
		remindMeLaterButton: formatMessage(messages.remindMeLaterButton),
	};
};
